import type { AccountPeriodBalance } from "@/apis/gql/types";
import { useTenant } from "@/providers/session";
import { gql, useQuery } from "@apollo/client";

export type PeriodGranularity = "monthly" | "quarterly";

const ACCOUNT_PERIOD_BALANCES_QUERY = gql`
  query GetAccountPeriodBalances(
    $tenantId: String!
    $startDate: String!
    $endDate: String!
    $granularity: String!
    $includeInactive: Boolean
    $excludeCounterparty: Boolean
  ) {
    accountPeriodBalances(
      tenantId: $tenantId
      startDate: $startDate
      endDate: $endDate
      granularity: $granularity
      includeInactive: $includeInactive
      excludeCounterparty: $excludeCounterparty
    ) {
      journalAccountId
      accountNumber
      accountName
      accountType
      entityType
      totalBalance
      hasActivity
      periodBalances {
        periodKey
        periodLabel
        balance
        debitTotal
        creditTotal
        entryCount
        startDate
        endDate
      }
    }
  }
`;

export interface GetAccountPeriodBalancesParams {
  startDate?: Date;
  endDate?: Date;
  granularity: PeriodGranularity;
  includeInactive?: boolean;
  excludeCounterparty?: boolean;
}

export const useGetAccountPeriodBalances = ({
  startDate,
  endDate,
  granularity,
  includeInactive = false,
  excludeCounterparty = false,
}: GetAccountPeriodBalancesParams) => {
  const { tenantId } = useTenant();

  const { data, loading, error, refetch } = useQuery<{
    accountPeriodBalances: AccountPeriodBalance[];
  }>(ACCOUNT_PERIOD_BALANCES_QUERY, {
    variables: {
      tenantId,
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
      granularity,
      includeInactive,
      excludeCounterparty,
    },
    skip: !tenantId || !startDate || !endDate,
  });

  return {
    data: data?.accountPeriodBalances || [],
    loading,
    error,
    refetch,
  };
};
